import { Link } from 'react-router';
import { PRESS_CONTACT_EMAIL } from '@/data/press-releases';

interface MediaContactProps {
  /** Optional named press contact for this release. */
  contactName?: string;
  /** Direct email for the named press contact. */
  contactEmail?: string;
  /** Optional interviews-availability note. */
  interviewsNote?: string;
}

/**
 * Shared media contact block. Reused on every press release page and kept
 * consistent with the /media-enquiries page (press email, one-working-day
 * response note, link through to the full media hub). When a release names a
 * specific contact, that contact leads and press@fixtyer.co.uk remains as the
 * general press address.
 */
export function MediaContact({ contactName, contactEmail, interviewsNote }: MediaContactProps) {
  const hasNamedContact = Boolean(contactName);

  return (
    <section className="rounded-2xl border border-brand-100 bg-brand-50 p-6 sm:p-8">
      <h2 className="font-display text-xl font-bold text-brand-950">Media contact</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          {hasNamedContact ? (
            <>
              <p className="text-sm font-semibold text-brand-950">{contactName}</p>
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="text-brand-600 hover:text-brand-700">
                  {contactEmail}
                </a>
              )}
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-brand-950">Press email</p>
              <a
                href={`mailto:${PRESS_CONTACT_EMAIL}`}
                className="text-brand-600 hover:text-brand-700"
              >
                {PRESS_CONTACT_EMAIL}
              </a>
            </>
          )}
        </div>
        <div>
          <p className="text-sm font-semibold text-brand-950">Response time</p>
          <p className="text-sm text-surface-600">
            We aim to respond to journalists within one working day, and faster where a
            deadline requires it.
          </p>
        </div>
      </div>

      {hasNamedContact && (
        <p className="mt-4 text-sm text-surface-600">
          General press enquiries:{' '}
          <a href={`mailto:${PRESS_CONTACT_EMAIL}`} className="text-brand-600 hover:text-brand-700">
            {PRESS_CONTACT_EMAIL}
          </a>
        </p>
      )}

      {interviewsNote && <p className="mt-4 text-sm text-surface-600">{interviewsNote}</p>}

      <p className="mt-5 text-sm text-surface-600">
        For interviews, data and background briefing, see our{' '}
        <Link
          to="/media-enquiries"
          className="font-medium text-brand-600 hover:text-brand-700 underline decoration-brand-300 underline-offset-2"
        >
          media enquiries
        </Link>{' '}
        page.
      </p>
    </section>
  );
}
